import { checkNativePlatform } from './nativeApiUtils';

/**
 * Utility functions for managing onboarding state
 */

// Local storage keys
const ONBOARDING_COMPLETED_KEY = 'sol_onboarding_completed';
const ONBOARDING_VERSION_KEY = 'sol_onboarding_version';

// Current onboarding version - increment this when onboarding content changes
const CURRENT_ONBOARDING_VERSION = '1.0.1';

/**
 * Check if user has completed onboarding for the current version
 * @returns {Promise<boolean>} - True if onboarding should be shown
 */
export const shouldShowOnboarding = async () => {
  try {
    // First check if we're on a native platform
    const isNative = await checkNativePlatform();
    
    if (!isNative) {
      console.log('[OnboardingUtils] Web platform detected - skipping onboarding');
      return false;
    }

    // FOR TESTING: Always show onboarding on native platforms
    // console.log('[OnboardingUtils] TESTING MODE: Always showing onboarding on native platforms');
    // return true;

    //COMMENTED OUT FOR TESTING - Uncomment for production
    // Check if onboarding was completed for current version
    const completedVersion = localStorage.getItem(ONBOARDING_VERSION_KEY);
    const isCompleted = localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';
    
    // Show onboarding if:
    // 1. Never completed before, OR
    // 2. Completed for an older version
    const shouldShow = !isCompleted || completedVersion !== CURRENT_ONBOARDING_VERSION;
    
    console.log('[OnboardingUtils] Onboarding check:', {
      isNative,
      completedVersion,
      currentVersion: CURRENT_ONBOARDING_VERSION,
      isCompleted,
      shouldShow
    });
    
    return shouldShow;
  } catch (error) {
    console.error('[OnboardingUtils] Error checking onboarding status:', error);
    // Default to not showing onboarding on error
    return false;
  }
};

/**
 * Mark onboarding as completed for the current version
 */
export const markOnboardingCompleted = () => {
  try {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    localStorage.setItem(ONBOARDING_VERSION_KEY, CURRENT_ONBOARDING_VERSION);
    
    console.log('[OnboardingUtils] Onboarding marked as completed for version:', CURRENT_ONBOARDING_VERSION);
  } catch (error) {
    console.error('[OnboardingUtils] Error saving onboarding completion:', error);
  }
};

/**
 * Mark onboarding as skipped (same as completed for tracking purposes)
 */
export const markOnboardingSkipped = () => {
  try {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    localStorage.setItem(ONBOARDING_VERSION_KEY, CURRENT_ONBOARDING_VERSION);
    
    console.log('[OnboardingUtils] Onboarding marked as skipped for version:', CURRENT_ONBOARDING_VERSION);
  } catch (error) {
    console.error('[OnboardingUtils] Error saving onboarding skip:', error);
  }
};

/**
 * Reset onboarding state (useful for development/testing)
 */
export const resetOnboardingState = () => {
  try {
    localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
    localStorage.removeItem(ONBOARDING_VERSION_KEY);
    
    console.log('[OnboardingUtils] Onboarding state reset');
  } catch (error) {
    console.error('[OnboardingUtils] Error resetting onboarding state:', error);
  }
};

/**
 * Get current onboarding state for debugging
 */
export const getOnboardingState = () => {
  try {
    return {
      completed: localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true',
      version: localStorage.getItem(ONBOARDING_VERSION_KEY),
      currentVersion: CURRENT_ONBOARDING_VERSION
    };
  } catch (error) {
    console.error('[OnboardingUtils] Error getting onboarding state:', error);
    return {
      completed: false,
      version: null,
      currentVersion: CURRENT_ONBOARDING_VERSION
    };
  }
};
